/**
 * Bar-chart waveform.
 *
 * SIMULATED. These bars are NOT amplitude data — there is no audio to analyse.
 * Heights come from a seeded PRNG (see src/lib/waveform.ts) so the same song
 * always renders the same shape. When real audio exists, this component keeps its
 * props and only the source of `heights` changes.
 *
 * The pulse animations reference `pulse0/1/2` keyframes defined in the global
 * stylesheet; they cannot be inline styles.
 */
import { c } from '../../styles/theme';

export interface WaveformProps {
  /** Bar heights in px. */
  heights: number[];
  /** Animates the bars and switches them to the accent colour. */
  playing?: boolean;
  /**
   * Spreads the bars across the container's full width instead of packing them at
   * a fixed 2px gap.
   *
   * Added for the Studio redesign so the waveform can sit inside the transport row
   * and span it, which is what makes it read as part of the player rather than as
   * a small chart parked next to one. The bars keep their 3px width — only the
   * spacing between them opens up, and `gap` stays as the minimum.
   */
  fill?: boolean;
  /**
   * Bar width in px. 3 everywhere the waveform is a panel of its own.
   *
   * Added for the small playing indicator that now stands in for the Play glyph on a
   * song row (see PlayingIndicator): inside a 32px circle, 3px bars with the 2px gap
   * below come to 18px and read as a chart squeezed into a button. 2.5px reads as
   * movement. Nothing else passes it, so every existing waveform is unchanged.
   */
  barWidth?: number;
  /** Overall height in px. The tallest bar should not exceed it. */
  height?: number;
  /**
   * Colour for the bars while playing, overriding the accent.
   *
   * Exists for a surface that draws the waveform on a saturated fill, where accent bars
   * on an accent background would be an invisible waveform. The stopped colour is not
   * overridable because the indicator is only ever drawn while playing — a stopped row
   * shows its play glyph instead.
   */
  color?: string;
  /**
   * Class for the outer box. Layout only.
   *
   * Added so PlayingIndicator can carry `.lyx-playing` — it is a fixed-width item on a
   * flex row now that it sits BESIDE each play/pause control rather than inside one, and
   * `flex-shrink: 0` is not something an inline style should own (see the mini player's
   * note in global.css). Nothing else passes it, so every existing waveform is unchanged.
   */
  className?: string;
}

export default function Waveform({
  heights,
  playing,
  fill,
  barWidth = 3,
  height = 24,
  color,
  className,
}: WaveformProps) {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 2,
        height,
        ...(fill ? { width: '100%', justifyContent: 'space-between' } : null),
      }}
      // Purely decorative: it conveys no information a screen reader can use, and
      // the adjacent PlayerBar already announces position.
      aria-hidden="true"
    >
      {heights.map((h, i) => (
        <div
          key={i}
          style={{
            width: barWidth,
            height: h,
            borderRadius: 2,
            background: playing ? (color ?? c.accentFill) : c.borderStrong,
            transition: 'background 0.2s',
            animation: playing ? `pulse${i % 3} 900ms ease-in-out infinite` : 'none',
            animationDelay: `${i * 40}ms`,
          }}
        />
      ))}
    </div>
  );
}
